import { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import FixedSave from "../../../../components/Actions/FixedSave";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import { get, post } from "../../../../utils/helpers/api";

function emptyRow() {
  return {
    id: Date.now(),
    product_id: null,
    product_name: '',
    description: '',
    quantity: 1,
    unit_cost: 0
  };
}

function lineTotal(quantity, unitCost) {
  return parseFloat(quantity || 0) * parseFloat(unitCost || 0);
}

function CreateBillOfMaterialsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [saveLoading, setSaveLoading] = useState(false);
  const [reference, setReference] = useState(state?.billOfMaterialData?.reference ?? '');

  const [billOfMaterialData, setBillOfMaterialData] = useState({
    product_id: state?.billOfMaterialData?.product_id ?? null,
    product_name: state?.billOfMaterialData?.product?.name ?? '',
    name: state?.billOfMaterialData?.name ?? '',
    quantity: state?.billOfMaterialData?.quantity ?? 1,
    is_active: state?.billOfMaterialData?.is_active ?? true,
    notes: state?.billOfMaterialData?.notes ?? ''
  });

  const [rows, setRows] = useState(
    state?.billOfMaterialData?.lines?.length > 0
      ? state.billOfMaterialData.lines.map((line, index) => ({
          id: Date.now() + index,
          product_id: line.product_id,
          product_name: line.product_name ?? '',
          description: line.description ?? '',
          quantity: line.quantity,
          unit_cost: line.unit_cost
        }))
      : [emptyRow()]
  );

  useEffect(() => {
    if (!id) {
      loadCreateData();
    }
  }, [id]);

  const loadCreateData = async () => {
    const data = await get('/production/bills-of-materials/create');
    if (data?.success) setReference(data.reference);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setBillOfMaterialData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddRow = () => setRows([...rows, emptyRow()]);
  const handleDeleteRow = (rowId) => setRows(rows.filter(row => row.id !== rowId));

  const updateRow = (rowId, changes) => {
    setRows(prev => prev.map(row => (row.id === rowId ? { ...row, ...changes } : row)));
  };

  const totalCost = rows.reduce((acc, row) => acc + lineTotal(row.quantity, row.unit_cost), 0);
  const costPerUnit = billOfMaterialData.quantity > 0 ? totalCost / billOfMaterialData.quantity : 0;

  const submit = async () => {
    if (!billOfMaterialData.product_id) return toast.error(t('production.finishedProductRequired'));
    if (!billOfMaterialData.quantity || Number(billOfMaterialData.quantity) < 1) return toast.error(t('production.batchQuantityRequired'));

    const validRows = rows.filter(row => row.product_id && Number(row.quantity) >= 1);
    if (validRows.length === 0) return toast.error(t('production.atLeastOneComponent'));

    setSaveLoading(true);
    try {
      const url = id
        ? '/production/bills-of-materials/update/' + id
        : '/production/bills-of-materials/store';

      const data = await post(url, {
        ...billOfMaterialData,
        quantity: Number(billOfMaterialData.quantity),
        lines: validRows.map(row => ({
          product_id: row.product_id,
          product_name: row.product_name,
          description: row.description,
          quantity: Number(row.quantity),
          unit_cost: Number(row.unit_cost)
        }))
      });

      if (data?.success) {
        toast.success(t('production.billOfMaterialsSaved'));
        navigate('/production/bills-of-materials');
      } else {
        toast.error(data?.message || t('common.somethingWentWrong'));
      }
    } catch (err) {
      toast.error(err?.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[
            t('sidebar.billsOfMaterials'),
            id ? (reference || t('common.edit')) : t('production.newBillOfMaterials')
          ]}
          onBack={() => navigate('/production/bills-of-materials')}
        />

        <div className="subheader-page-content scrollable">
          <Row className="g-3 mb-3">
            <Col md="4">
              <Label>{t('production.finishedProduct')}: <span className="text-danger">*</span></Label>
              <CustomSelect
                module="inventory/products"
                value={billOfMaterialData.product_id
                  ? { value: billOfMaterialData.product_id, label: billOfMaterialData.product_name }
                  : null}
                onChange={(selected) => setBillOfMaterialData(prev => ({
                  ...prev,
                  product_id: selected ? selected.value : null,
                  product_name: selected ? selected.label : ''
                }))}
                placeholder={t('inventory.searchProduct')}
                createLink="/inventory/products/create"
              />
            </Col>

            <Col md="2">
              <Label>{t('production.batchQuantity')}: <span className="text-danger">*</span></Label>
              <Input
                type="number"
                name="quantity"
                min="1"
                step="1"
                value={billOfMaterialData.quantity}
                onChange={onChange}
              />
            </Col>

            <Col md="3">
              <Label>{t('common.name')}:</Label>
              <Input
                name="name"
                value={billOfMaterialData.name}
                onChange={onChange}
                placeholder={t('production.billOfMaterialsNamePlaceholder')}
              />
            </Col>

            <Col md="3">
              <Label>{t('common.reference')}:</Label>
              <Input value={reference || t('production.generatedOnSave')} disabled />
            </Col>
          </Row>

          <p className="text-muted small mb-3">{t('production.batchQuantityHint')}</p>

          <Table bordered className="prod-lines-table">
            <thead>
              <tr>
                <th>{t('production.component')}</th>
                <th>{t('common.description')}</th>
                <th width="120px">{t('common.quantity')}</th>
                <th width="140px">{t('production.unitCost')}</th>
                <th width="140px">{t('common.total')}</th>
                <th width="40px"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((data) => (
                <tr key={data.id}>
                  <td style={{ minWidth: 220 }}>
                    <CustomSelect
                      module="inventory/products"
                      value={data.product_id ? { value: data.product_id, label: data.product_name } : null}
                      onChange={(selected, objectData) => updateRow(data.id, {
                        product_id: selected ? selected.value : null,
                        product_name: selected ? selected.label : '',
                        unit_cost: objectData?.cost_price ?? data.unit_cost
                      })}
                      placeholder={t('inventory.searchProduct')}
                      createLink="/inventory/products/create"
                    />
                  </td>
                  <td>
                    <Input
                      value={data.description}
                      onChange={(e) => updateRow(data.id, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={data.quantity}
                      onChange={(e) => updateRow(data.id, { quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={data.unit_cost}
                      onChange={(e) => updateRow(data.id, { unit_cost: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input value={lineTotal(data.quantity, data.unit_cost).toFixed(2)} disabled />
                  </td>
                  <td>
                    <IoMdClose className="prod-line-remove" onClick={() => handleDeleteRow(data.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <DefaultButton color="light" size="sm" className="mb-4" onClick={handleAddRow}>
            + {t('common.addNewRow')}
          </DefaultButton>

          <Row className="g-3 mb-3">
            <Col md="8">
              <FormGroup>
                <Label>{t('common.notes')}:</Label>
                <Input
                  type="textarea"
                  name="notes"
                  rows="4"
                  value={billOfMaterialData.notes}
                  onChange={onChange}
                />
              </FormGroup>

              <FormGroup check>
                <Input
                  type="checkbox"
                  id="bom-is-active"
                  checked={!!billOfMaterialData.is_active}
                  onChange={(e) => setBillOfMaterialData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                <Label check for="bom-is-active">{t('production.activeBillOfMaterials')}</Label>
              </FormGroup>
            </Col>

            <Col md="4">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">{t('production.totalComponentCost')}</span>
                <strong>{totalCost.toFixed(2)}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">{t('production.costPerUnit')}</span>
                <strong>{costPerUnit.toFixed(2)}</strong>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <FixedSave onSave={submit} loading={saveLoading} disabled={saveLoading} />
    </SidebarLayout>
  );
}

export default CreateBillOfMaterialsPage;
